import { useState } from 'react';
import { Stack, Text, Textarea, Button, Group, Mark } from '@mantine/core';
import { commentRepository } from '@ayocore/comments/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Task } from '@ayocore/tasks/schemas/task.schema';

/**
 * Контент модалки добавления комментария к изменению задачи (открывается через useModal).
 * Используется при изменении статуса или переносе срока задачи.
 */
export function TaskCommentModal({
  onClose,
  task,
  context,
  onCommentSaved,
}: {
  /** Закрыть модалку (вызывается и при «Пропустить», и после сохранения). */
  onClose: () => void;
  task: Task;
  context: {
    statusChanged: boolean;
    movedDateForward: boolean;
    newDate: Date | null;
  };
  onCommentSaved?: () => void;
}) {
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    setSavingComment(true);
    try {
      // Создаем комментарий к связанной сущности (лиду или партнёру)
      const commentContent = `Комментарий к задаче "#${task.id} ${task.title}": ${commentText.trim()}`;
      const targetEntityType =
        task.entity_type === 'crm::PartnerRepository'
          ? 'crm::PartnerRepository'
          : 'crm::LeadRepository';

      await commentRepository.create({
        entity_uuid: task.entity_uuid!,
        entity_type: targetEntityType,
        content: commentContent,
      });

      logger.info('Comment created after task update', {
        task_id: task.id,
        entity_type: targetEntityType,
        entity_uuid: task.entity_uuid,
      });

      // Очистка и закрытие
      setCommentText('');
      onClose();

      // Вызываем callback если передан
      if (onCommentSaved) {
        onCommentSaved();
      }
    } catch (error) {
      logger.error('Failed to create comment:', error);
      // Показываем ошибку, но все равно закрываем форму
      onClose();
      if (onCommentSaved) {
        onCommentSaved();
      }
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentCancel = () => {
    setCommentText('');
    onClose();
    if (onCommentSaved) {
      onCommentSaved();
    }
  };

  return (
    <Stack gap='md'>
      <Text size='sm' c='dimmed'>
        {context.statusChanged && <>Статус изменён; пожалуйста, укажите причину изменения.</>}
        {context.statusChanged && context.movedDateForward && ' '}
        {context.movedDateForward && context.newDate && (
          <>
            Вы изменили дату на <Mark>{context.newDate.toLocaleDateString('ru-RU')}</Mark>, уточните
            причину.
          </>
        )}
      </Text>

      <Textarea
        value={commentText}
        onChange={e => setCommentText(e.currentTarget.value)}
        placeholder='Введите комментарий...'
        minRows={4}
        autosize
        disabled={savingComment}
      />

      <Group justify='flex-end'>
        <Button variant='outline' onClick={handleCommentCancel} disabled={savingComment}>
          Пропустить
        </Button>
        <Button
          onClick={handleCommentSubmit}
          loading={savingComment}
          disabled={!commentText.trim()}
        >
          Сохранить
        </Button>
      </Group>
    </Stack>
  );
}
